import type { BaseModel } from './baseModel';
import type { Profile } from './profile';

/**
 * ConnectionType classifies a pairwise relationship between two profiles.
 *
 * The `share_dish` / `share_app` variants are write-once at share time —
 * they record HOW the relationship started and cannot be set or changed by
 * the FE. Only `friend` / `family` / `connection` (generic) are user-settable
 * (see {@link UserSettableConnectionType}).
 */
export type ConnectionType =
  | 'share_dish'
  | 'share_app'
  | 'friend'
  | 'family'
  | 'connection';

/**
 * The subset of ConnectionType the initiator may choose when creating a
 * request or PATCH a connection to. Mirrors the backend's
 * `UserMutableConnectionTypes` allow-list.
 */
export type UserSettableConnectionType = 'friend' | 'family' | 'connection';

/**
 * The marketing-campaign QR-code row a connection was auto-created from,
 * as surfaced by `?expand=qrCode` on `GET /connections`. Present only on
 * connections whose `qrCodeId` was stamped by the campaign fan-out; absent
 * otherwise. The FE reads `connectionGroup` to render the italicized group
 * label under the type text, and `connectionType`/`connectionLevel` inform
 * the type-lock UX (see `isFromQrCode`).
 */
export interface ConnectionQrCode {
  id: string;
  connectionLevel?: number | null;
  connectionType?: ConnectionType | null;
  connectionCampaign?: string | null;
  connectionGroup?: string | null;
  connectionProfile?: string | null;
}

/**
 * A connection row as returned by the backend. Each row is a pairwise
 * relationship between an initiator (`profileId`) and a recipient
 * (`connectedProfileId`, nullable until the recipient resolves the request).
 *
 * A connection is "live" / accepted iff `acceptedAt` is set, `rejectedAt`
 * is null, and `deletedAt` is null. Pending = both `acceptedAt` and
 * `rejectedAt` are null.
 */
export interface Connection extends BaseModel {
  /** The initiator — the profile that sent the request. */
  profileId: string;
  /** The recipient — null until the request is resolved to a known profile. */
  connectedProfileId?: string | null;
  type: ConnectionType;
  /** Set when the recipient accepts (or at creation for share-driven rows). */
  acceptedAt?: string | null;
  /** Set when the recipient declines. Mutually exclusive with acceptedAt. */
  rejectedAt?: string | null;

  /**
   * The email an initiator invited a not-yet-registered recipient with
   * (shell-recipient flow). Present only on the initiator's own outgoing rows
   * until the invite is resolved; null for known-profile or share rows.
   */
  invitedEmail?: string | null;

  /**
   * FK to the marketing QR code that auto-created this row (campaign fan-out).
   * Non-null identifies the row as campaign-driven — type is provenance and
   * PATCH is blocked server-side.
   */
  qrCodeId?: string | null;

  /**
   * The initiator's profile, preloaded by the list endpoint. For
   * `requests_to_me` this is always the other party ("who's asking").
   */
  profile?: Profile | null;
  /**
   * The recipient's profile, preloaded by the list endpoint. Null for
   * unresolved shell rows (recipient added by email, not yet a known profile).
   */
  connectedProfile?: Profile | null;
  /**
   * Preloaded when the caller asked for `?expand=qrCode`. Absent otherwise,
   * so callers should treat both `qrCodeId` being null AND `qrCode` being
   * undefined as "not campaign-driven".
   */
  qrCode?: ConnectionQrCode | null;
}

/**
 * True when a connection was auto-created by a marketing QR fan-out. Prefer
 * this over reading `qrCodeId` / `qrCode` directly at call sites — the
 * response shape depends on whether the caller asked for `expand=qrCode`,
 * and this helper covers both.
 */
export function isFromQrCode(c: Connection): boolean {
  return !!c.qrCodeId || !!c.qrCode;
}

/**
 * Request body for `POST /connections`. At least one of `connectedProfileId`
 * and `email` must be provided:
 *   - `connectedProfileId` — the FE already knows the recipient's profile
 *     (the display-name "Connect" entry point on dish/order cards).
 *   - `email` — the "Connect with Friends" flow; the backend resolves the
 *     profile or sends a shell-recipient invite.
 *
 * Phone is never accepted — probing whether a number is on the system would
 * leak account presence.
 */
export interface CreateConnectionRequest {
  /** The recipient's profile id. Used by the display-name "Connect" entry point. */
  connectedProfileId?: string;
  /** The recipient's email. Used by the "Connect with Friends" flow. */
  email?: string;
  /** Optional. Omitted → backend defaults to `connection` (generic). */
  type?: UserSettableConnectionType;
}

/**
 * Maps a stored connection type to the user-facing taxonomy we surface in the
 * UI: friend / family / connection. Share-origin types (`share_dish`,
 * `share_app`) are kept on the row as write-once provenance but are displayed
 * generically as "connection" — we don't expose how two people met. The
 * initiator can still explicitly re-categorize to friend/family.
 */
export function effectiveConnectionType(
  type: ConnectionType,
): UserSettableConnectionType {
  if (type === 'friend' || type === 'family') return type;
  return 'connection';
}

/** Segments for `GET /connections?segment=…`. Default is `all`. */
export type ConnectionSegment =
  | 'accepted'
  | 'requests_to_me'
  | 'requests_from_me'
  | 'all';

/** Response shape for `GET /connections?segment=…`. */
export interface ListConnectionsResponse {
  connections: Connection[];
}
